import React, { FC, memo, useState } from 'react';
import { WarehouseForBooking, getWorkingScheduleString } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { calculateDistance } from 'widgets/SelectWarehouseMap/utils/helpers/calculateDistance';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { ReadMore } from 'shared/ui/ReadMore';
import { ReactComponent as LocationIcon } from 'shared/assets/icons/newIcons/LocationIcon.svg';
import { ReactComponent as TimeIcon } from 'shared/assets/icons/newIcons/TimeIcon.svg';
import { ReactComponent as BoxIcon } from 'shared/assets/icons/newIcons/BoxIcon.svg';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import WarehouseDefaultImage from 'shared/assets/images/WarehouseDefaultImage.png';
import { Box } from 'entities/Box';
import { ContractInfo } from 'entities/Contract';
import { ModernSelectedBoxInfoCard } from 'widgets/ModernContractInformationForm/ui/ModernSelectedBoxInfoCard';
import { ModernPriceDetailsCard } from 'widgets/ModernContractInformationForm/ui/ModernPriceDetailsCard';
import { ModernTotalToPayCard } from 'widgets/ModernContractInformationForm/ui/ModernTotalToPayCard';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface MobileBookingInfoStackProps {
  warehouseInfo: WarehouseForBooking;
  boxInfo?: Nullable<Box>;
  contractOptions?: Nullable<ContractInfo>;
}

export const MobileBookingInfoStack: FC<MobileBookingInfoStackProps> = memo((props) => {
  const { warehouseInfo, boxInfo, contractOptions } = props;
  const { t } = useAppTranslation(['booking', 'common']);
  const userCoords = useGeolocation();
  const [isCollapseOpen, setIsCollapseOpen] = useState(true);

  const warehouseImage = warehouseInfo?.imageUrl || WarehouseDefaultImage;
  const fullAddress = `${warehouseInfo?.address || ''} ${warehouseInfo?.zip || ''}`;

  const distance = userCoords
    ? roundNumber(calculateDistance(userCoords.lat, userCoords.lng, warehouseInfo.latitude, warehouseInfo.longitude))
    : null;

  const workingScheduleString = getWorkingScheduleString(warehouseInfo.workingSchedule, t);

  const sizeRange = warehouseInfo?.sizeCodes?.length
    ? `${formatDecimal(Math.min(...warehouseInfo.sizeCodes.map((sc) => sc.square)))} м² - ${formatDecimal(
        Math.max(...warehouseInfo.sizeCodes.map((sc) => sc.square)),
      )} м²`
    : '';

  const warehouseFeatures = [{ label: t('Cameras monitoring') }, { label: t('24 / 7 access') }, { label: t('Online payments') }];

  // Add office feature if warehouse has offices
  if (warehouseInfo?.hasOffices) {
    warehouseFeatures.push({
      label: t('Office rent'),
    });
  }

  return (
    <div className="bg-backgroundColor rounded-none">
      {/* Arrow Row - Always visible at the bottom */}

      {!isCollapseOpen && (
        <div className="py-1">
          <div
            className="bg-white rounded-lg flex justify-center py-3 cursor-pointer"
            onClick={() => {
              setIsCollapseOpen((prev) => !prev);
            }}
          >
            <CollapseArrow className={`ease-linear duration-200 fill-primaryLight ${isCollapseOpen ? '-rotate-90' : 'rotate-90'}`} />
          </div>
        </div>
      )}

      {/* Collapsible Content - Entire warehouse info */}
      {isCollapseOpen && (
        <div className="relative space-y-1">
          {/* Warehouse Image */}
          <div className="w-full h-[250px] rounded-lg overflow-hidden">
            <img src={warehouseImage} alt={warehouseInfo?.name || 'Warehouse'} className="w-full h-full object-cover" />
          </div>

          {/* 1) Info Card (overlapping the image higher) */}
          <div className="relative -mt-12 -translate-y-[15px] bg-white rounded-lg overflow-hidden">
            {/* Basic Info */}
            <div className="p-4 space-y-3">
              <h2 className="text-xl font-bold text-primary font-tektur">{warehouseInfo?.name}</h2>

              <div className="space-y-2">
                <div className="flex items-center text-grey">
                  <LocationIcon className="w-[18px] h-[18px] mr-2 text-grey flex-shrink-0" />
                  <span className="text-sm">{fullAddress}</span>
                  {distance && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="text-sm font-medium text-grey">{distance} км от вас</span>
                    </>
                  )}
                </div>

                {workingScheduleString && (
                  <div className="flex items-center text-grey">
                    <TimeIcon className="w-[18px] h-[18px] mr-2 text-grey flex-shrink-0" />
                    <span className="text-sm">{workingScheduleString}</span>
                  </div>
                )}

                {sizeRange && (
                  <div className="flex items-center text-grey">
                    <BoxIcon className="w-[16px] h-[16px] mr-2 [&>path]:stroke-grey" />
                    <span className="text-sm">{sizeRange}</span>
                  </div>
                )}

                <div className="text-sm text-accent font-medium">
                  {t('{{availableBoxesAmount}} boxes free', { availableBoxesAmount: warehouseInfo.availableBoxesAmount })}
                </div>
              </div>

              {/* Additional Info */}
              {warehouseInfo?.description && (
                <div className="mt-4">
                  <div className="text-lg font-semibold text-primary mb-2">{t('Description')}</div>
                  <ReadMore className="text-grey" html={getLocalizedString(warehouseInfo.description)} rows={2} />
                </div>
              )}
            </div>
          </div>

          {/* 2) Selected Box Info */}
          {boxInfo && (
            <div className="bg-white rounded-lg overflow-hidden -translate-y-[15px]">
              <ModernSelectedBoxInfoCard boxInfo={boxInfo} warehouseInfo={warehouseInfo} accountType={contractOptions?.accountType} />
            </div>
          )}

          {/* 3) Price Details */}
          {contractOptions && (
            <div className="bg-white rounded-lg overflow-hidden -translate-y-[15px]">
              <ModernPriceDetailsCard contractInfo={contractOptions} />
            </div>
          )}

          {/* 4) Total To Pay */}
          {contractOptions && (
            <div className="bg-white rounded-lg overflow-hidden -translate-y-[15px]">
              <ModernTotalToPayCard contractInfo={contractOptions} />
            </div>
          )}
          <div
            className="bg-white rounded-lg flex justify-center py-2 cursor-pointer -translate-y-[15px]"
            onClick={() => {
              setIsCollapseOpen((prev) => !prev);
            }}
          >
            <CollapseArrow className={`ease-linear duration-200 fill-primaryLight ${isCollapseOpen ? '-rotate-90' : 'rotate-90'}`} />
          </div>
        </div>
      )}
    </div>
  );
});
